import React from "react";
import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  FieldValues,
  Path,
} from "react-hook-form";

import { FormControl } from "../../../components";
import { Theme } from "../../../components/ThemedChakraProvider/ThemedChakraProvider";
import { PositionListItemFragment } from "../../graphql";
import PositionSelect from "../PositionSelect";

type PositionFieldProps<T extends FieldValues & { positionId?: string }> = {
  control: Control<T>;
  errors: FieldErrors<T>;
  isRequired: boolean;
  theme: Theme;
  setSelectedPosition: (position: PositionListItemFragment | undefined) => void;
};

export const PositionField = <T extends FieldValues & { positionId?: string }>({
  control,
  errors,
  isRequired,
  theme,
  setSelectedPosition,
}: PositionFieldProps<T>): React.ReactElement => (
  <FormControl
    error={errors.positionId as FieldError | undefined}
    displayName="Which position is this AI interviewer for?"
    helpText="Will not be displayed to candidates"
    helpTextProps={{ fontSize: "xs" }}
  >
    <Controller
      name={"positionId" as Path<T>}
      control={control}
      rules={{
        required: isRequired ? "Position is required" : false,
        validate: (value) => {
          if (
            isRequired &&
            (!value || (typeof value === "string" && value.trim() === ""))
          ) {
            return "Position is required";
          }
          return true;
        },
      }}
      render={({ field, fieldState }) => (
        <PositionSelect
          positionId={field.value}
          onSelect={(position: PositionListItemFragment | undefined) => {
            setSelectedPosition(position);
            field.onChange(position?.id ?? "");
          }}
          customStyles={{
            control: (provided: Record<string, any>) => ({
              ...provided,
              borderRadius: theme.radii.md,
              borderColor: fieldState.error ? theme.colors.red[500] : undefined,
              borderWidth: fieldState.error ? "2px" : provided.borderWidth,
              "&:hover": {
                borderColor: fieldState.error
                  ? theme.colors.red[500]
                  : undefined,
                borderWidth: fieldState.error ? "2px" : provided.borderWidth,
              },
            }),
          }}
        />
      )}
    />
  </FormControl>
);
